import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AppHeader from '../components/AppHeader';
import AppFooter from '../components/AppFooter';
import AnalyzeForm from '../components/AnalyzeForm';
import LoadingState from '../components/LoadingState';
import ResultsView from '../components/ResultsView';
import { analyzeImage, ApiError } from '../api/client';
import { clearAccessCode } from '../api/accessCode';
import './AnalyzePage.css';

const STATUS = {
  IDLE: 'IDLE',
  LOADING: 'LOADING',
  DONE: 'DONE',
};

export default function AnalyzePage() {
  const [status, setStatus] = useState(STATUS.IDLE);
  const [data, setData] = useState(null);
  const [imageUrl, setImageUrl] = useState('');
  const [error, setError] = useState('');
  const abortRef = useRef(null);
  const navigate = useNavigate();

  // Abort any in-flight request when the component unmounts
  useEffect(() => () => abortRef.current?.abort(), []);

  const handleAnalyze = async (url) => {
    setError('');
    setStatus(STATUS.LOADING);
    setImageUrl(url);
    setData(null);

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const result = await analyzeImage(url, controller.signal);
      setData(result);
      setStatus(STATUS.DONE);
    } catch (err) {
      if (err.name === 'AbortError') return;
      // 401 → kick back to gate so the user can re-enter their code
      if (err instanceof ApiError && err.status === 401) {
        clearAccessCode();
        navigate('/gate', { replace: true });
        return;
      }
      setError(err instanceof ApiError ? err.message : 'Unexpected error. Please try again.');
      setStatus(STATUS.IDLE);
    }
  };

  const handleReset = () => {
    setStatus(STATUS.IDLE);
    setData(null);
    setError('');
    setImageUrl('');
  };

  const headerStatus =
    status === STATUS.LOADING ? 'ANALYZING…' : status === STATUS.DONE ? 'COMPLETE' : 'READY';

  return (
    <div className="analyze-page">
      <AppHeader status={headerStatus} />

      <main className="analyze-page__main">
        {status === STATUS.IDLE && (
          <AnalyzeForm onSubmit={handleAnalyze} disabled={false} error={error} />
        )}
        {status === STATUS.LOADING && <LoadingState />}
        {status === STATUS.DONE && data && (
          <ResultsView data={data} imageUrl={imageUrl} onReset={handleReset} />
        )}
      </main>

      <AppFooter requestId={data?.request_id} />
    </div>
  );
}
